import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import {
  fetchDashboardLayout,
  resetDashboardLayout,
  saveDashboardLayout,
  type DashboardLayoutResponse,
  type WidgetInstance,
} from '../../request/dashboards/layout.ts';
import { dashboardLayoutQueryKeys } from '../../queries/dashboards/keys.ts';
import type { PersonaKey } from '../../components/analytics/widgetRegistry.tsx';

/**
 * Self-serve dashboard layout: fetch + save (debounced by the caller) + reset,
 * for a single persona dashboard key (Feature 5.3, wave 13). Save/reset both
 * invalidate the layout query so the page re-syncs with the server's copy.
 */
export function useDashboardLayoutQuery(key: PersonaKey): UseQueryResult<DashboardLayoutResponse, Error> {
  return useQuery({
    queryKey: dashboardLayoutQueryKeys.layout(key),
    queryFn: () => fetchDashboardLayout(key),
  });
}

export function useSaveDashboardLayout(key: PersonaKey) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (layout: WidgetInstance[]) => saveDashboardLayout(key, layout),
    onSuccess: (data) => {
      queryClient.setQueryData(dashboardLayoutQueryKeys.layout(key), data);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: dashboardLayoutQueryKeys.layout(key) });
    },
  });
}

export function useResetDashboardLayout(key: PersonaKey) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => resetDashboardLayout(key),
    onSuccess: (data) => {
      queryClient.setQueryData(dashboardLayoutQueryKeys.layout(key), data);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: dashboardLayoutQueryKeys.layout(key) });
    },
  });
}
